<template>
  <div class="mt-5">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <h4 class="mb-0 font-serif text-body-emphasis">
        {{ t("ecolabels.labels.heading") }}
      </h4>
      <button class="btn btn-primary btn-sm text-nowrap" @click="openCreate">
        <font-awesome-icon icon="plus" class="me-1" />
        {{ t("ecolabels.labels.new_label") }}
      </button>
    </div>

    <div class="card border-translucent">
      <div class="card-body p-0 overflow-x-auto">
        <DataTable
          v-model:filters="filters"
          :value="rows"
          dataKey="id"
          :loading="loading"
          :rowClass="rowClass"
          rowHover
          filterDisplay="row"
          sortMode="single"
          removableSort
          class="ecolabel-datatable"
          tableClass="content-sized-table label-list-table"
        >
          <template #empty>
            <div class="text-center py-4 text-muted">
              {{ t("ecolabels.labels.empty") }}
            </div>
          </template>
          <template #loading>
            <LoadingIndicatorComponent />
          </template>

          <Column
            field="number_text"
            sortField="number"
            :header="t('ecolabels.labels.columns.number')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
            class="text-nowrap tabular-nums"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('common.filter')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="name"
            :header="t('ecolabels.labels.columns.name')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #body="{ data }">
              {{ labelNameFirstLine(data.name) }}
              <span v-if="data.has_entities" class="text-body-tertiary">
                ·
                {{
                  t("ecolabels.labels.entities_suffix", {
                    count: entityCount(data),
                  })
                }}
              </span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('common.filter')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>
          <Column
            v-if="isAdmin"
            field="company_name"
            :header="t('ecolabels.labels.columns.company')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('common.filter')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="period_start"
            :header="t('ecolabels.labels.columns.period')"
            sortable
            class="text-nowrap tabular-nums"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #body="{ data }">
              {{ formatLabelPeriod(data) }}
            </template>
          </Column>
          <Column
            field="total_mwh"
            :header="t('ecolabels.labels.columns.total')"
            sortable
            dataType="numeric"
            class="text-end text-nowrap tabular-nums"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #body="{ data }">
              {{ formatNumberWithSpaces(data.total_mwh) }}
            </template>
          </Column>
          <Column
            field="countries"
            :header="t('ecolabels.labels.columns.countries')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <DebouncedTextFilter
                v-model="filterModel.value"
                :placeholder="t('common.filter')"
                @update:modelValue="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="issued_at"
            :header="t('ecolabels.labels.columns.issued')"
            sortable
            class="text-nowrap tabular-nums"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #body="{ data }">
              {{ formatDateTimeIntoDate(data.issued_at) }}
            </template>
          </Column>
          <Column
            field="status"
            :header="t('ecolabels.labels.columns.status')"
            sortable
            :showFilterMatchModes="false"
            :showFilterMenu="false"
          >
            <template #sorticon="{ sortOrder }">
              <ColumnSortIcon :sortOrder />
            </template>
            <template #body="{ data }">
              <span class="badge" :class="labelStatusPillClass(data.status)">
                {{
                  t(`ecolabels.labels.statuses.${data.status.toLowerCase()}`)
                }}
              </span>
              <div
                v-if="data.reopened_label_id !== null"
                class="small text-body-tertiary text-nowrap"
              >
                {{ t("ecolabels.labels.reopened_as_draft") }}
              </div>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <MultiSelect
                v-model="filterModel.value"
                :options="statusOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('common.filter')"
                class="p-column-filter w-100"
                display="chip"
                appendTo="body"
                :focusOnHover="false"
                @change="filterCallback()"
              >
                <template #value="{ value, placeholder }">
                  <span
                    v-if="value && value.length"
                    class="text-primary fw-bold"
                  >
                    {{ t("common.filter") }}
                  </span>
                  <span v-else>{{ placeholder }}</span>
                </template>
              </MultiSelect>
            </template>
          </Column>
          <Column :header="t('common.actions.title')" class="text-nowrap">
            <template #body="{ data }">
              <div class="d-flex gap-1">
                <button
                  class="btn btn-sm btn-phoenix-secondary"
                  @click="openDetail(data)"
                >
                  {{ t("ecolabels.labels.actions.open") }}
                </button>
                <button
                  v-if="data.can_download"
                  class="btn btn-sm btn-phoenix-secondary"
                  :disabled="downloadingId === data.id"
                  @click="downloadPdf(data)"
                >
                  <font-awesome-icon icon="download" class="me-1" />
                  {{ t("ecolabels.labels.actions.download") }}
                </button>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>

    <LabelWorkbenchModal
      v-if="workbenchOpen"
      :label="workbenchLabel"
      :isAdmin
      :catalog
      :catalogLoading
      @close="workbenchOpen = false"
      @changed="loadLabels"
    />
  </div>
</template>

<script setup lang="ts">
import ColumnSortIcon from "@/components/common/ColumnSortIcon.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import {
  formatDateTimeIntoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable, { type DataTableFilterMeta } from "primevue/datatable";
import MultiSelect from "primevue/multiselect";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import DebouncedTextFilter from "../DebouncedTextFilter.vue";
import type { EcolabelDevice } from "../api/ecolabelsTypes";
import LabelWorkbenchModal from "./LabelWorkbenchModal.vue";
import {
  entityCount,
  formatLabelPeriod,
  labelListRows,
  labelNameFirstLine,
  labelStatusPillClass,
} from "./labelDisplay";
import { labelsApi, pdfErrorMessage } from "./labelsApi";
import type { Label, LabelStatus } from "./labelsTypes";

const LABEL_STATUSES: LabelStatus[] = ["DRAFT", "ISSUED", "VOIDED"];

defineProps<{
  isAdmin: boolean;
  catalog: EcolabelDevice[];
  catalogLoading: boolean;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalWarningHandler } = useGlobalHandlers();

const labels = ref<Label[]>([]);
const loading = ref(false);
const workbenchOpen = ref(false);
const workbenchLabel = ref<Label | null>(null);
const downloadingId = ref<number | null>(null);
const filters = ref<DataTableFilterMeta>({
  number_text: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  company_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  countries: { value: null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: null, matchMode: FilterMatchMode.IN },
});

const rows = computed(() =>
  labelListRows(labels.value, t("ecolabels.labels.draft_number"))
);

const statusOptions = computed(() =>
  LABEL_STATUSES.map((status) => ({
    label: t(`ecolabels.labels.statuses.${status.toLowerCase()}`),
    value: status,
  }))
);

onMounted(loadLabels);

async function loadLabels() {
  loading.value = true;
  try {
    labels.value = (await labelsApi.listLabels()).data.labels;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
}

function openCreate() {
  workbenchLabel.value = null;
  workbenchOpen.value = true;
}

function openDetail(label: Label) {
  workbenchLabel.value = label;
  workbenchOpen.value = true;
}

async function downloadPdf(label: Label) {
  downloadingId.value = label.id;
  try {
    const response = await labelsApi.downloadLabelPdf(label.id);
    downloadFromApiResponse(
      response.data,
      response.headers,
      `${label.number ?? "label"}.pdf`
    );
  } catch (error) {
    const message = await pdfErrorMessage(error);
    if (message === null) globalErrorHandler(error);
    else globalWarningHandler(message, "");
  } finally {
    downloadingId.value = null;
  }
}

function rowClass(label: Label): string | undefined {
  return label.status === "VOIDED" ? "text-body-quaternary" : undefined;
}
</script>

<style lang="scss" scoped>
@use "../ecolabel-form-shared";

:deep(.label-list-table) {
  min-width: 56rem;
}
</style>
